/*
 * ─────────────────────────────────────────────────────────────────────────────
 * VECCHI FILE GESTIONE CORSI — sostituiti dalla gestione in
 * src/jsx/pages/struttura/corsi/. Non sono più raggiungibili dal menu: restano
 * solo per gli indirizzi diretti, finché non si decide di eliminarli.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Tab, Nav, Modal, Button, Spinner, Form } from "react-bootstrap";
import { Information, Calendar, Add, Trash, Warning2, Judge, TickCircle } from "iconsax-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MyApiClient from "../../../../../api-client/MyApiClient";
import Select from "react-select";
import { useProfile } from "../../../../../hooks/useProfile";
import { useMemberships } from "../../../../../hooks/useMemberships";
import { sports } from "../../../../components/Util/enum";
import chiediConferma from '../../../../components/Util/chiediConferma';

export const UserCoursesTeamsNew = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const { profile: profileData } = useProfile({ 
        autoFetch: true, 
        includeTitles: true 
    });
    const { getConfirmedMembers, memberships, loading: membershipsLoading } = useMemberships({ autoFetch: true });

    const [loadingShowed, setLoadingShowed] = useState(false);
    const [confirmShowed, setConfirmShowed] = useState(false);
    const [onContinue, setOnContinue] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [tabIndex, setTabIndex] = useState(0);

    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const [availableCourses, setAvailableCourses] = useState([]);
    const [availableCoaches, setAvailableCoaches] = useState([]);
    const [availableFacilities, setAvailableFacilities] = useState([]);
    const [filteredCoaches, setFilteredCoaches] = useState([]);
    const [filteredFacilities, setFilteredFacilities] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    // Stati per la gestione dei conflitti
    const [conflictsData, setConflictsData] = useState(null);
    const [loadingConflicts, setLoadingConflicts] = useState(false);
    const [hasConflicts, setHasConflicts] = useState(false);

    const dayOptions = [
        { value: 'MONDAY', label: 'Lunedì' },
        { value: 'TUESDAY', label: 'Martedì' },
        { value: 'WEDNESDAY', label: 'Mercoledì' },
        { value: 'THURSDAY', label: 'Giovedì' },
        { value: 'FRIDAY', label: 'Venerdì' },
        { value: 'SATURDAY', label: 'Sabato' },
        { value: 'SUNDAY', label: 'Domenica' }
    ];

    const [formData, setFormData] = useState({
        bookingCourseId: '',
        name: '',
        coachId: '',
        venueId: '',
        weeklySchedule: [],
        startDate: '',
        endDate: '',
        maxMembers: 10
    });

    // Load courses, coaches and facilities on page load
    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);
                    const [coursesData, facilitiesData] = await Promise.all([
                        apiClient.getBookingCourses(token),
                        apiClient.getVenues(token)
                    ]);
                    setLoadingShowed(false);

                    // Filter only OPEN courses with CONFIRMED status
                    if (coursesData && Array.isArray(coursesData)) {
                        // console.log('All courses data:', coursesData);
                        const confirmedOpenCourses = coursesData.filter(course => {
                            // console.log(`Course: ${course.name}, Type: ${course.type}, Status: ${course.status}`);
                            return course.type === 'OPEN' && course.status === 'PURCHASABLE';
                        });
                        // console.log('Filtered confirmed open courses:', confirmedOpenCourses);
                        const formattedCourses = confirmedOpenCourses.map(course => {
                            // Trova la decodifica dello sport
                            const sportLabel = sports.find(sport => sport.value === course.sportType)?.label || course.sportType;
                            return {
                                value: course.id,
                                label: `${course.name} - ${sportLabel}`,
                                course: course
                            };
                        });
                        setAvailableCourses(formattedCourses);
                    }

                    // Load facilities
                    if (facilitiesData) {
                        const facilitiesArray = Array.isArray(facilitiesData) ? facilitiesData : 
                                               (facilitiesData.data && Array.isArray(facilitiesData.data)) ? facilitiesData.data :
                                               (facilitiesData.venues && Array.isArray(facilitiesData.venues)) ? facilitiesData.venues : [];
                        
                        const formattedFacilities = facilitiesArray.map(facility => ({
                            value: facility.id,
                            label: facility.name,
                            sportType: facility.sportType
                        }));
                        setAvailableFacilities(formattedFacilities);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoadingShowed(false);
                setModalTitle("Errore");
                setModalText("Errore durante il caricamento dei dati");
                setModalShowed(true);
            }
        };

        fetchData();
    }, []);

    // Auto-selezione del corso se presente nell'URL
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const courseId = urlParams.get('courseId');
        
        if (courseId && availableCourses.length > 0) {
            // Timeout per simulare il click dell'utente dopo che tutto è caricato
            setTimeout(() => {
                const courseToSelect = availableCourses.find(course => course.value === courseId);
                if (courseToSelect) {
                    handleSelectChange(courseToSelect, 'bookingCourseId');
                }
            }, 1000); // 1 secondo di delay
        }
    }, [availableCourses, location.search]);

    // Load coaches when memberships are available
    useEffect(() => {
        if (!membershipsLoading && memberships) {
            const coaches = getConfirmedMembers('COACH');
            if (coaches && Array.isArray(coaches)) {
                const formattedCoaches = coaches.map(coach => ({
                    value: coach.userId,
                    label: coach.name,
                    sportType: coach.sport
                }));
                setAvailableCoaches(formattedCoaches);
            }
        }
    }, [memberships, membershipsLoading, getConfirmedMembers]);

    // Effetto per chiamare automaticamente checkConflicts quando si completa la programmazione settimanale
    useEffect(() => {
        if (tabIndex === 2) {
            checkConflicts();
        }
    }, [tabIndex]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSelectChange = (selectedOption, name) => {
        const selectedValue = selectedOption?.value || '';
        
        setFormData(prev => ({
            ...prev,
            [name]: selectedValue,
        }));

        // When a course is selected, populate form with course data
        if (name === 'bookingCourseId' && selectedOption) {
            const course = selectedOption.course;
            setSelectedCourse(course);
            setFormData(prev => ({
                ...prev,
                startDate: course.startDate || '',
                endDate: course.endDate || '',
                coachId: '', // Reset coach selection
                venueId: ''  // Reset venue selection
            }));
            
            // Filter coaches and facilities based on course sport type
            if (course.sportType) {
                const filteredCoachesForSport = availableCoaches.filter(coach => 
                    coach.sportType === course.sportType
                );
                const filteredFacilitiesForSport = availableFacilities.filter(facility => 
                    facility.sportType === course.sportType
                );
                
                setFilteredCoaches(filteredCoachesForSport);
                setFilteredFacilities(filteredFacilitiesForSport);
            }
        }
    };

    const addScheduleItem = () => {
        setFormData(prev => ({
            ...prev,
            weeklySchedule: [...prev.weeklySchedule, {
                id: Date.now(),
                dayOfWeek: '',
                startTime: '',
                endTime: ''
            }]
        }));
    };

    const removeScheduleItem = (id) => {
        setFormData(prev => ({
            ...prev,
            weeklySchedule: prev.weeklySchedule.filter(item => item.id !== id)
        }));
    };

    const updateScheduleItem = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            weeklySchedule: prev.weeklySchedule.map(item => 
                item.id === id ? { ...item, [field]: value } : item
            )
        }));
    };

    const checkConflicts = async () => {
        if (!formData.weeklySchedule || formData.weeklySchedule.length === 0) {
            setConflictsData(null);
            return;
        }

        try {
            setLoadingConflicts(true);
            
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                const response = await apiClient.checkCourseConflicts(token, {
                    coachId: formData.coachId,
                    venueId: formData.venueId,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    weeklySchedule: formData.weeklySchedule,
                    sport: formData.sportType
                });
                
                if (response && !response.error) {
                    setConflictsData(response);
                    setHasConflicts(response.summary && response.summary.conflictSlots > 0);
                } else {
                    console.error('Errore nel controllo conflitti:', response?.error);
                    setModalTitle("Errore");
                    setModalText("Errore durante il controllo dei conflitti. Riprova.");
                    setModalShowed(true);
                }
            }
        } catch (error) {
            console.error('Errore nel controllo conflitti:', error);
            setModalTitle("Errore");
            setModalText("Errore durante il controllo dei conflitti. Riprova.");
            setModalShowed(true);
        } finally {
            setLoadingConflicts(false);
        }
    };

    const saveAndActivate = () => {
        setOnContinue(() => () => saveDataAndActivate());
        setConfirmShowed(true);
        setConfirmText("Sei sicuro di volere continuare? La squadra sarà creata e attivata immediatamente.");
    };

    const saveDataAndActivate = async () => {
        if (!formData.bookingCourseId) {
            setModalTitle("Errore");
            setModalText("Seleziona un corso");
            setModalShowed(true);
            return;
        }
        if (!formData.name) {
            setModalTitle("Errore");
            setModalText("Inserisci il nome della squadra");
            setModalShowed(true);
            return;
        }
        if (!formData.coachId) {
            setModalTitle("Errore");
            setModalText("Seleziona uno staff tecnico");
            setModalShowed(true);
            return;
        }
        if (!formData.venueId) {
            setModalTitle("Errore");
            setModalText("Seleziona un impianto");
            setModalShowed(true);
            return;
        }
        if (formData.weeklySchedule.length === 0) {
            setModalTitle("Errore");
            setModalText("Aggiungi almeno una programmazione settimanale");
            setModalShowed(true);
            return;
        }

        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();

                setLoadingShowed(true);

                const teamData = {
                    bookingCourseId: formData.bookingCourseId,
                    name: formData.name,
                    coachId: formData.coachId,
                    venueId: formData.venueId,
                    weeklySchedule: formData.weeklySchedule.map(item => ({
                        dayOfWeek: item.dayOfWeek,
                        startTime: item.startTime,
                        endTime: item.endTime
                    })),
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    maxMembers: parseInt(formData.maxMembers),
                    status: 'DRAFT'
                };

                const createResult = await apiClient.createCourseTeam(token, teamData);

                if (createResult && createResult.error) {
                    setLoadingShowed(false);
                    setModalTitle("Errore durante l'operazione");
                    setModalText(createResult.error);
                    setModalShowed(true);
                    return;
                }

                const activateResult = await apiClient.updateCourseTeam(token, createResult.data.id, { status: 'ACTIVE' });
                setLoadingShowed(false);

                if (activateResult && activateResult.error) {
                    setModalTitle("Errore durante l'attivazione");
                    setModalText(activateResult.error);
                    setModalShowed(true);
                } else {
                    navigate('/struttura/booking/userCoursesTeams');
                }
            }
        } catch (error) {
            setLoadingShowed(false);
            setModalTitle("Errore durante l'operazione");
            setModalText('Errore generico');
            setModalShowed(true);
        }
    };

    const handleDraftSaveClick = async () => {
        if (!formData.name) {
            setModalTitle("Errore");
            setModalText("Inserisci almeno il nome della squadra per salvare come bozza");
            setModalShowed(true);
            return;
        }

        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();

                setLoadingShowed(true);

                const teamData = {
                    bookingCourseId: formData.bookingCourseId || undefined,
                    name: formData.name,
                    coachId: formData.coachId || undefined,
                    venueId: formData.venueId || undefined,
                    weeklySchedule: formData.weeklySchedule.map(item => ({
                        dayOfWeek: item.dayOfWeek,
                        startTime: item.startTime,
                        endTime: item.endTime
                    })),
                    startDate: formData.startDate || undefined,
                    endDate: formData.endDate || undefined,
                    maxMembers: formData.maxMembers ? parseInt(formData.maxMembers) : undefined,
                    status: 'DRAFT'
                };

                const response = await apiClient.createCourseTeam(token, teamData);
                setLoadingShowed(false);

                if (response.error) {
                    setModalTitle("Errore");
                    setModalText(response.error);
                    setModalShowed(true);
                } else {
                    setModalTitle("Bozza salvata");
                    setModalText("La squadra è stata salvata come bozza");
                    setOnContinue(() => () => {
                        navigate('/struttura/booking/userCoursesTeams');
                    });
                    setModalShowed(true);
                }
            }
        } catch (error) {
            setLoadingShowed(false);
            setModalTitle("Errore");
            setModalText("Errore durante il salvataggio della bozza");
            setModalShowed(true);
        }
    };

    const goBack = (numOfTab) => {
        if(numOfTab > -1) {
            setTabIndex(numOfTab);
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        } else {
            navigate('/struttura/booking/userCoursesTeams');
        }
    };

    const handleNextStep = () => {
        if (tabIndex < 3) {
            setTabIndex(tabIndex + 1);
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }
    };

    const validateCurrentStep = () => {
        switch(tabIndex) {
            case 0: // Informazioni
                if (!formData.bookingCourseId) {
                    setModalTitle("Errore");
                    setModalText("Seleziona un corso");
                    setModalShowed(true);
                    return false;
                }
                if (!formData.name) {
                    setModalTitle("Errore");
                    setModalText("Inserisci il nome della squadra");
                    setModalShowed(true);
                    return false;
                }
                if (!formData.coachId) {
                    setModalTitle("Errore");
                    setModalText("Seleziona uno staff tecnico");
                    setModalShowed(true);
                    return false;
                }
                if (!formData.venueId) {
                    setModalTitle("Errore");
                    setModalText("Seleziona un impianto");
                    setModalShowed(true);
                    return false;
                }
                if (!formData.maxMembers || formData.maxMembers < 1) {
                    setModalTitle("Errore");
                    setModalText("Inserisci il numero massimo di membri (minimo 1)");
                    setModalShowed(true);
                    return false;
                }
                return true;
            case 1: // Programmazione
                if (!formData.startDate) {
                    setModalTitle("Errore");
                    setModalText("Inserisci la data di inizio");
                    setModalShowed(true);
                    return false;
                }
                if (!formData.endDate) {
                    setModalTitle("Errore");
                    setModalText("Inserisci la data di fine");
                    setModalShowed(true);
                    return false;
                }
                if (new Date(formData.startDate) >= new Date(formData.endDate)) {
                    setModalTitle("Errore");
                    setModalText("La data di fine deve essere successiva alla data di inizio");
                    setModalShowed(true);
                    return false;
                }
                if (formData.weeklySchedule.length === 0) {
                    setModalTitle("Errore");
                    setModalText("Aggiungi almeno una programmazione settimanale");
                    setModalShowed(true);
                    return false;
                }
                return true;
            case 2: // Conflitti
                return true;
            default:
                return true;
        }
    };

    const handleStepNavigation = () => {
        if (validateCurrentStep()) {
            handleNextStep();
        }
    };

    const tabData = [
        {
            id: "informazioni",
            name: "Informazioni",
            icon: <Information size="20" className="me-2" />,
            content: (
                <>
                    <Card>
                        <Card.Header><h4 className="card-title">Informazioni Squadra</h4></Card.Header>
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Seleziona Corso Multiplo *</Form.Label>
                                            <Select
                                                value={availableCourses.find(option => option.value === formData.bookingCourseId) || null}
                                                onChange={(selectedOption) => handleSelectChange(selectedOption, 'bookingCourseId')}
                                                options={availableCourses}
                                                placeholder="Seleziona un corso multiplo confermato"
                                                isSearchable
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome Squadra *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Inserisci il nome della squadra"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Staff Tecnico *</Form.Label>
                                            <Select
                                                value={filteredCoaches.find(option => option.value === formData.coachId) || null}
                                                onChange={(selectedOption) => handleSelectChange(selectedOption, 'coachId')}
                                                options={filteredCoaches}
                                                placeholder="Seleziona staff tecnico"
                                                isSearchable
                                                isDisabled={!selectedCourse}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Impianto *</Form.Label>
                                            <Select
                                                value={filteredFacilities.find(option => option.value === formData.venueId) || null}
                                                onChange={(selectedOption) => handleSelectChange(selectedOption, 'venueId')}
                                                options={filteredFacilities}
                                                placeholder="Seleziona un impianto"
                                                isSearchable
                                                isDisabled={!selectedCourse}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Numero Massimo Membri *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="maxMembers"
                                                value={formData.maxMembers}
                                                onChange={handleChange}
                                                min="1"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                    
                    <div className="d-flex justify-content-end gap-4">
                        <button type="button" className="btn btn-dark light" onClick={() => goBack(-1)}>Annulla</button>
                        <Link onClick={handleDraftSaveClick} to="#" className="btn btn-secondary">Salva come bozza</Link>
                        <Link onClick={handleStepNavigation} to="#" className="btn btn-primary">Avanti</Link>
                    </div>
                </>
            )
        },
        {
            id: "programmazione",
            name: "Programmazione",
            icon: <Calendar size="20" className="me-2" />,
            content: (
                <>
                    <Card>
                        <Card.Header><h4 className="card-title">Programmazione Settimanale</h4></Card.Header>
                        <Card.Body>
                            <Form>
                                <Row className="mb-4">
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Data Inizio *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col className="col-xl-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Data Fine *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6>Orari Settimanali</h6>
                                    <Button variant="outline-primary" size="sm" className="btn-add" onClick={addScheduleItem}>
                                        <Add size="14" className="me-1" />
                                        Aggiungi Programmazione
                                    </Button>
                                </div>

                                {formData.weeklySchedule.map((item, index) => (
                                    <Row key={item.id || index} className="mb-3 align-items-end">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Giorno della settimana</Form.Label>
                                                <Select
                                                    value={dayOptions.find(option => option.value === item.dayOfWeek) || null}
                                                    onChange={(selectedOption) => updateScheduleItem(item.id, 'dayOfWeek', selectedOption?.value || '')}
                                                    options={dayOptions}
                                                    placeholder="Seleziona giorno"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Ora inizio</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={item.startTime}
                                                    onChange={(e) => updateScheduleItem(item.id, 'startTime', e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Ora fine</Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={item.endTime}
                                                    onChange={(e) => updateScheduleItem(item.id, 'endTime', e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                onClick={async () => { if (await chiediConferma({ titolo: 'Togliere la fascia?', testo: 'La fascia oraria sparisce dal corso.' })) removeScheduleItem(item.id); }}
                                                className="w-100"
                                            >
                                                <Trash size="14" />
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}

                                {formData.weeklySchedule.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <p>Nessuna programmazione aggiunta. Clicca su "Aggiungi Programmazione" per iniziare.</p>
                                    </div>
                                )}
                            </Form>
                        </Card.Body>
                    </Card>
                    
                    <div className="d-flex justify-content-end gap-4">
                        <Link onClick={() => goBack(0)} to="#" className="btn btn-dark light">Indietro</Link>
                        <Link onClick={handleDraftSaveClick} to="#" className="btn btn-secondary">Salva come bozza</Link>
                        <Link onClick={handleStepNavigation} to="#" className="btn btn-primary">Avanti</Link>
                    </div>
                </>
            )
        },
        {
            id: 'conflitti',
            name: "Conflitti",
            icon: <Warning2 size='18' />,
            content: (
                <>
                    <Card>
                        <Card.Header><h4 className="card-title">Gestione conflitti</h4></Card.Header>
                        <Card.Body>
                            <Form noValidate>
                                <Row>
                                    <Col className="col-xl-12">
                                        {loadingConflicts ? (
                                            <div className="text-center py-4">
                                                <Spinner animation="border" role="status">
                                                    <span className="visually-hidden">Caricamento...</span>
                                                </Spinner>
                                                <p className="mt-2">Controllo conflitti in corso...</p>
                                            </div>
                                        ) : conflictsData ? (
                                            <div>
                                                {/* Riepilogo conflitti */}
                                                {conflictsData.summary && (
                                                    <div className={`alert ${hasConflicts ? 'alert-warning' : 'alert-success'}`} role="alert">
                                                        <Warning2 variant="Bulk" className="me-2" />
                                                        <strong>
                                                            {hasConflicts 
                                                                ? `Trovati ${conflictsData.summary.conflictSlots} conflitti su ${conflictsData.summary.totalSlots} slot`
                                                                : 'Nessun conflitto rilevato'
                                                            }
                                                        </strong>
                                                    </div>
                                                )}
                                                
                                                <div className="mt-3">
                                                    {console.log('Debug conflictsData:', conflictsData)}
                                                    {conflictsData && conflictsData.slots && conflictsData.slots.length > 0 ? (
                                                        <div className="table-responsive">
                                                            <table className="table">
                                                                <thead className="table-dark">
                                                                    <tr>
                                                                        <th className="text-black">Giorno</th>
                                                                        <th className="text-black">Orario</th>
                                                                        <th className="text-center text-black" style={{width: '50%'}}>Stato</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {conflictsData.slots.map((slot, index) => {
                                                                        const dayLabel = dayOptions.find(d => d.value === slot.dayOfWeek)?.label || slot.dayOfWeek;
                                                                        
                                                                        // Verifica se questo slot ha conflitti dalla risposta API
                                                                        const hasSlotConflict = !slot.isValid;
                                                                        const conflictReasons = [];
                                                                        
                                                                        if (slot.venueConflict?.hasConflict) {
                                                                            conflictReasons.push(slot.venueConflict.reason || 'Conflitto struttura');
                                                                        }
                                                                        if (slot.coachConflict?.hasConflict) {
                                                                            conflictReasons.push(slot.coachConflict.reason || 'Conflitto maestro');
                                                                        }
                                                                        
                                                                        const conflictMessage = conflictReasons.join('; ');
                                                                        
                                                                        return (
                                                                            <tr key={index} className={hasSlotConflict ? '' : ''} style={hasSlotConflict ? {backgroundColor: '#f8d7da'} : {backgroundColor: '#f8f9fa'}}>
                                                                                <td className="fw-medium">
                                                                                    <span className="badge bg-primary me-2">{dayLabel.substring(0, 3).toUpperCase()}</span>
                                                                                    <small>
                                                                                        {slot.date ? new Date(slot.date).toLocaleDateString('it-IT', {
                                                                                            day: '2-digit',
                                                                                            month: '2-digit',
                                                                                            year: 'numeric'
                                                                                        }) : 'N/A'}
                                                                                    </small>
                                                                                </td>
                                                                                <td className="fw-medium">
                                                                                    <i className="fa fa-clock text-muted me-2"></i>
                                                                                    {slot.startTime} - {slot.endTime}
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {hasSlotConflict ? (
                                                                                        <div>
                                                                                            <span className="badge bg-danger fs-6 mb-1">
                                                                                                <i className="fa fa-exclamation-triangle me-1"></i>
                                                                                                CONFLITTO
                                                                                            </span>
                                                                                            {conflictMessage && (
                                                                                                <div className="small text-danger mt-1">
                                                                                                    <strong>Motivo:</strong> {conflictMessage}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="badge bg-success fs-6">
                                                                                            <i className="fa fa-check me-1"></i>
                                                                                            DISPONIBILE
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="alert alert-warning" role="alert">
                                                            <Warning2 variant="Bulk" className="me-2" />
                                                            <strong>Nessuno slot trovato per il controllo conflitti</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Assicurati di aver compilato la programmazione settimanale nel tab precedente.
                                                                {conflictsData && conflictsData.slots ? `Trovati ${conflictsData.slots.length} slot.` : 'Dati conflitti non disponibili.'}
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <div className="alert alert-info" role="alert">
                                                    <Information variant="Bulk" className="me-2" />
                                                    <strong>Controllo conflitti in attesa...</strong>
                                                    <br />
                                                    <small>Completa la programmazione settimanale nel tab precedente per visualizzare l'analisi dei conflitti</small>
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                    
                    <div className="d-flex justify-content-end gap-4">
                        <Link onClick={() => goBack(1)} to="#" className="btn btn-dark light">Indietro</Link>
                        <Link onClick={handleDraftSaveClick} to="#" className="btn btn-secondary">Salva come bozza</Link>
                        <Link
                            onClick={hasConflicts ? undefined : handleStepNavigation}
                            to="#"
                            className={`btn btn-primary ${hasConflicts ? 'disabled' : ''}`}
                            style={hasConflicts ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                        >
                            Avanti
                        </Link>
                    </div>
                </>
            )
        },
        {
            id: "riepilogo",
            name: "Riepilogo e conferma",
            icon: <Judge size="20" className="me-2" />,
            content: (
                <>
                    <Card>
                        <Card.Header><h4 className="card-title">Riepilogo Squadra</h4></Card.Header>
                        <Card.Body>
                            <Row>
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Nome Squadra</Form.Label>
                                        <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={formData.name || ''} />
                                    </div>
                                </Col>
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Corso</Form.Label>
                                        <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={selectedCourse?.name || ''} />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mt-4">
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Sport</Form.Label>
                                        <Select
                                            isDisabled
                                            value={sports.find(s => s.value === selectedCourse?.sportType) || null}
                                            options={sports}
                                            style={{lineHeight: '40px', color: '#fd7e14', paddingLeft: '15px'}}
                                        />
                                    </div>
                                </Col>
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Staff Tecnico</Form.Label>
                                        <Select
                                            isDisabled
                                            value={filteredCoaches.find(c => c.value === formData.coachId) || null}
                                            options={filteredCoaches}
                                            style={{lineHeight: '40px', color: '#fd7e14', paddingLeft: '15px'}}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mt-4">
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Impianto</Form.Label>
                                        <Select
                                            isDisabled
                                            value={filteredFacilities.find(f => f.value === formData.venueId) || null}
                                            options={filteredFacilities}
                                            style={{lineHeight: '40px', color: '#fd7e14', paddingLeft: '15px'}}
                                        />
                                    </div>
                                </Col>
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Max Membri</Form.Label>
                                        <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={formData.maxMembers || ''} />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mt-4">
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Data Inizio</Form.Label>
                                        <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={formData.startDate || ''} />
                                    </div>
                                </Col>
                                <Col className="col-xl-6">
                                    <div className="mb-3">
                                        <Form.Label>Data Fine</Form.Label>
                                        <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={formData.endDate || ''} />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mt-4">
                                <Col className="col-xl-12">
                                    <Form.Label>Programmazione Settimanale</Form.Label>
                                    {formData.weeklySchedule.length > 0 ? (
                                        <div className="mt-2">
                                            {formData.weeklySchedule.map((schedule, index) => (
                                                <div key={index} className="border rounded p-2 mb-2" style={{backgroundColor: '#f2f2f2', color: 'rgba(108,117,125,0.6)', fontSize: '14px'}}>
                                                    <strong>{dayOptions.find(d => d.value === schedule.dayOfWeek)?.label}</strong>{' '}
                                                    {schedule.startTime} - {schedule.endTime}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value="Nessuna programmazione definita" />
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                    
                    <div className="d-flex justify-content-end gap-4">
                        <Link onClick={() => goBack(2)} to="#" className="btn btn-dark light">Indietro</Link>
                        <Link onClick={handleDraftSaveClick} to="#" className="btn btn-secondary">Salva come bozza</Link>
                        <Link onClick={saveAndActivate} to="#" className="btn btn-primary">Salva e attiva</Link>
                    </div>
                </>
            )
        }
    ];

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close">
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-primary btn-add" onClick={() => {
                        setModalShowed(false);
                        if (onContinue) onContinue();
                    }}>
                        {onContinue ? 'Continua' : 'Chiudi'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={confirmShowed} centered>
                <Modal.Header>
                    <Modal.Title>Conferma</Modal.Title>
                    <Button onClick={() => setConfirmShowed(false)} variant="" className="btn-close">
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64" dangerouslySetInnerHTML={{ __html: confirmText }}></Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConfirmShowed(false)}>
                        Annulla
                    </Button>
                    <Button className="btn btn-primary btn-add" onClick={() => {
                        setConfirmShowed(false);
                        if (onContinue) onContinue();
                    }}>
                        Continua
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="custom-tab-1">
                <Tab.Container activeKey={tabData[tabIndex]?.id.toLowerCase()}>
                    <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                        {tabData.map((data, i) => (
                            <Nav.Item as="li" key={i} className="tabMenu">
                                <Nav.Link 
                                    onClick={(e) => e.preventDefault()} 
                                    eventKey={data.id.toLowerCase()}
                                    style={{ cursor: 'default', pointerEvents: 'none' }}
                                >
                                    {data.name}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>
                    <Row className="align-items-start mt-4">
                        <div className="col-12">
                            <Tab.Content>
                                {tabData.map((data, i) => (
                                    <Tab.Pane eventKey={data.id.toLowerCase()} key={i}>
                                        {data.content}
                                    </Tab.Pane>
                                ))}
                            </Tab.Content>
                        </div>
                    </Row>
                </Tab.Container>
            </div>
        </>
    );
};

export default UserCoursesTeamsNew;